import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, FileText, Edit3, Trash2, Plus, BookOpen, FileText as Files02Icon, Bookmark, MessageCircle, HelpCircle, Layers, CheckSquare, Upload } from 'react-feather';
import { useSidePanel } from '../context/SidePanelContext';
import { useGeneratedContent } from '../context/GeneratedContentContext';
import Resources from '../views/resources';
import StudyNotesView from './StudyNotesView';
import QuotesView from './QuotesView';
import { getAllFiles, deleteFileFromStorage, base64ToFile, saveFileToStorage } from '../utils/fileStorage';
import styles from '../styles/resourcesSidebar.module.css';

// Custom AI Editing Icon (HugeIcons style)
const AiEditingIcon = ({ size = 24, color = 'currentColor', strokeWidth = 1.5 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
    <path d="M16.5 5L19 7.5M5 19L5.5 16.5L16 6L18 8L7.5 18.5L5 19Z" />
    <path d="M12 3C7.02944 3 3 7.02944 3 12" />
    <path d="M21 12C21 16.9706 16.9706 21 12 21" />
    <circle cx="12" cy="3" r="1" fill={color} stroke="none" />
    <circle cx="21" cy="12" r="1" fill={color} stroke="none" />
  </svg>
);

// Custom AI File Icon (HugeIcons style)
const AiFileIcon = ({ size = 24, color = 'currentColor', strokeWidth = 1.5 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" />
    <path d="M14 2V8H20" />
    <circle cx="12" cy="15" r="2" />
    <path d="M9 15H7" />
    <path d="M17 15H15" />
    <path d="M12 12V10" />
    <path d="M12 18V20" />
  </svg>
);

const ResourcesSidebar = ({ isOpen = true }) => {
  const [documents, setDocuments] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // Track which document is being deleted
  const [generatedDeleteConfirm, setGeneratedDeleteConfirm] = useState(null); // Track which generated item is being deleted
  const [uploading, setUploading] = useState(false);
  
  const [expandedSections, setExpandedSections] = useState({
    documents: false,
    resources: false,
    studyTools: false,
    recentlyGenerated: false
  });
  const [expandedTool, setExpandedTool] = useState(null); // Track which study tool has its doc dropdown open
  
  const { openComponentPanel, openFilePanel } = useSidePanel();
  const { generatedContent, deleteGeneratedContent, renameGeneratedContent } = useGeneratedContent();
  const fileInputRef = useRef(null);

  // Handle file upload directly
  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        await saveFileToStorage(file);
      }
      // Reload documents and notify other components
      const allFiles = getAllFiles();
      setDocuments(allFiles);
      window.dispatchEvent(new CustomEvent('filesUpdated'));
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Trigger file input click
  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  // Load documents from all storage (localStorage + sessionStorage) on mount and listen for changes
  useEffect(() => {
    const loadDocuments = () => {
      try {
        const allFiles = getAllFiles();
        setDocuments(allFiles);
      } catch (error) {
        console.error('Error loading documents:', error);
      }
    };

    // Initial load
    loadDocuments();

    // Listen for storage changes (when files are uploaded/deleted in resources tab)
    const handleStorageChange = (e) => {
      if (e.key === 'quizmate_files') {
        loadDocuments();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events from the same tab
    const handleFilesUpdate = () => {
      loadDocuments();
    };
    
    window.addEventListener('filesUpdated', handleFilesUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('filesUpdated', handleFilesUpdate);
    };
  }, []);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleItemClick = (item, type) => {
    console.log('handleItemClick called with:', { type, item });
    if (type === 'document') {
      // Convert base64 back to File object for opening in side panel
      const file = base64ToFile(item.content, item.name, item.type);
      openFilePanel(file);
      // Close sidebar after opening document
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('closeSidebar'));
      }, 100);
    } else if (type === 'resources') {
      console.log('Opening Resources tab...');
      openComponentPanel(<Resources />, 'Resources');
      // Close sidebar after opening resources
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('closeSidebar'));
      }, 100);
    }
  };
  
  const handleDocumentAction = (action, item, e) => {
    e.stopPropagation();
    if (action === 'rename') {
      const newName = prompt('Enter new name:', item.name);
      if (newName && newName !== item.name) {
        try {
          const updatedDocuments = documents.map(doc => 
            doc.id === item.id ? { ...doc, name: newName } : doc
          );
          localStorage.setItem('quizmate_files', JSON.stringify(updatedDocuments));
          setDocuments(updatedDocuments);
          // Notify other components
          window.dispatchEvent(new CustomEvent('filesUpdated'));
        } catch (error) {
          console.error('Error renaming document:', error);
        }
      }
    } else if (action === 'delete') {
      // Set the document for deletion confirmation
      setDeleteConfirm(item.id);
    }
  };
  
  // Handle confirming deletion
  const handleConfirmDelete = (itemId) => {
    try {
      deleteFileFromStorage(itemId);
      setDocuments(prev => prev.filter(doc => doc.id !== itemId));
      // Notify other components
      window.dispatchEvent(new CustomEvent('filesUpdated'));
      setDeleteConfirm(null); // Clear confirmation state
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };
  
  // Handle canceling deletion
  const handleCancelDelete = () => {
    setDeleteConfirm(null);
  };

  const ResourceSection = ({ title, icon: Icon, items, type, sectionKey, color, showCreateButton = false, isOpen: sidebarOpen = true }) => {
    const isExpanded = expandedSections[sectionKey];
    const hasItems = items && items.length > 0;
    
    const handleSectionClick = () => {
      if (type === 'resources') {
        handleItemClick({}, 'resources');
      } else if (type === 'document' && !hasItems) {
        // If no documents, open resources tab
        handleItemClick({}, 'resources');
      } else {
        toggleSection(sectionKey);
      }
    };
    
    const handlePlusClick = (e) => {
      e.stopPropagation(); // Prevent section toggle when clicking plus
      console.log('ResourcesSidebar plus button clicked - opening resources tab');
      handleItemClick({}, 'resources');
    };
    
    return (
      <div className={styles.section}>
        <div 
          className={styles.sectionHeader}
          onClick={handleSectionClick}
        >
          <div className={styles.sectionTitle}>
            <Icon size={20} style={{ color }} />
            <span className={`${styles.sectionText} ${!sidebarOpen ? styles.hidden : ''}`}>{title}</span>
          </div>
          <div className={`${styles.sectionActions} ${!sidebarOpen ? styles.hidden : ''}`}>
            {type === 'document' && (
              <button 
                className={styles.plusButton}
                onClick={handlePlusClick}
                title="Open Resources tab"
              >
                <Plus size={14} />
              </button>
            )}
            {type !== 'resources' && hasItems && (
              <div className={`${styles.chevron} ${isExpanded ? styles.expanded : ''}`}>
                <ChevronRight size={14} />
              </div>
            )}
          </div>
        </div>
        
        {type !== 'resources' && (
          <div className={`${styles.sectionContent} ${isExpanded && hasItems && sidebarOpen ? styles.expanded : styles.collapsed}`}>
            {hasItems && (
              <div className={styles.itemsList}>
                {items.map((item, index) => (
                  <div
                    key={item.id || index}
                    className={styles.item}
                    onClick={() => handleItemClick(item, type)}
                  >
                    <div className={styles.itemContent}>
                      <div className={styles.itemName}>
                        {item.name || item.title}
                      </div>
                    </div>
                    {type === 'document' && (
                      <div className={styles.itemActions}>
                        {deleteConfirm === item.id ? (
                          // Show inline confirmation
                          <div className={styles.deleteConfirmation}>
                            <span className={styles.deleteText}>Delete?</span>
                            <button 
                              className={`${styles.actionButton} ${styles.confirmButton}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleConfirmDelete(item.id);
                              }}
                              title="Yes, delete"
                            >
                              Yes
                            </button>
                            <button 
                              className={`${styles.actionButton} ${styles.cancelButton}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelDelete();
                              }}
                              title="Cancel"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          // Show normal action buttons
                          <>
                            <button 
                              className={styles.actionButton}
                              onClick={(e) => handleDocumentAction('rename', item, e)}
                              title="Rename"
                            >
                              <Edit3 size={12} />
                            </button>
                            <button 
                              className={styles.actionButton}
                              onClick={(e) => handleDocumentAction('delete', item, e)}
                              title="Delete"
                            >
                              <Trash2 size={12} />
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const studyTools = [
    { id: 'notes', label: 'Study Notes', icon: Bookmark, color: '#667eea' },
    { id: 'quotes', label: 'Key Quotes', icon: MessageCircle, color: '#f093fb' },
    { id: 'quiz', label: 'Generate Quiz', icon: HelpCircle, color: '#34d399' },
    { id: 'flashcards', label: 'Flashcards', icon: Layers, color: '#fbbf24' },
    { id: 'test', label: 'Practice Test', icon: CheckSquare, color: '#ef4444' }
  ];

  const handleStudyToolClick = (toolId) => {
    // Toggle the document dropdown for this tool
    setExpandedTool(expandedTool === toolId ? null : toolId);
  };

  const handleToolWithDocument = (toolId, document) => {
    // Dispatch event to HomePage to handle the study tool action with selected document
    window.dispatchEvent(new CustomEvent('generateStudyContent', {
      detail: { 
        action: { id: toolId },
        document 
      }
    }));
    setExpandedTool(null);
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('closeSidebar'));
    }, 100);
  };

  // Open saved generated content
  const handleOpenGeneratedContent = (item) => {
    let component = null;
    
    if (item.type === 'notes') {
      component = <StudyNotesView content={item.data} documentName={item.documentName} />;
    } else if (item.type === 'quotes') {
      component = <QuotesView quotes={item.data} documentName={item.documentName} />;
    }
    
    if (component) {
      openComponentPanel(component, item.name);
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('closeSidebar'));
      }, 100);
    }
  };

  return (
    <div className={styles.resourcesSidebar}>
      {/* Hidden file input for direct uploads */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept=".pdf,.doc,.docx,.txt,.md"
        multiple
        style={{ display: 'none' }}
      />
      
      {/* Documents Section - with nice list UI */}
      <div className={styles.section}>
        <div 
          className={styles.sectionHeader}
          onClick={() => documents.length > 0 ? toggleSection('documents') : triggerFileUpload()}
        >
          <div className={styles.sectionTitle}>
            <BookOpen size={20} style={{ color: '#3b82f6' }} />
            <span className={`${styles.sectionText} ${!isOpen ? styles.hidden : ''}`}>Documents</span>
          </div>
          <div className={`${styles.sectionActions} ${!isOpen ? styles.hidden : ''}`}>
            <button 
              className={styles.plusButton}
              onClick={(e) => {
                e.stopPropagation();
                triggerFileUpload();
              }}
              title="Upload Document"
              disabled={uploading}
            >
              {uploading ? <Upload size={14} className={styles.spinning} /> : <Plus size={14} />}
            </button>
            {documents.length > 0 && (
              <div className={`${styles.chevron} ${expandedSections.documents ? styles.expanded : ''}`}>
                <ChevronRight size={14} />
              </div>
            )}
          </div>
        </div>
        
        <div className={`${styles.sectionContent} ${expandedSections.documents && isOpen && documents.length > 0 ? styles.expanded : styles.collapsed}`}>
          {documents.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No documents uploaded</p>
              <button 
                className={styles.uploadButton}
                onClick={triggerFileUpload}
                disabled={uploading}
              >
                {uploading ? <Upload size={14} className={styles.spinning} /> : <Plus size={14} />}
                {uploading ? 'Uploading...' : 'Upload Document'}
              </button>
            </div>
          ) : (
            <div className={styles.studyToolsDocList}>
              {documents.map((doc) => (
                <div 
                  key={doc.id} 
                  className={styles.studyToolsDocItem}
                  onClick={() => handleItemClick(doc, 'document')}
                >
                  <FileText size={14} />
                  <span className={styles.studyToolsDocName}>{doc.name}</span>
                  <ChevronRight size={12} className={styles.docChevron} />
                </div>
              ))}
              <div 
                className={`${styles.studyToolsDocItem} ${styles.uploadOption}`}
                onClick={triggerFileUpload}
              >
                {uploading ? <Upload size={14} className={styles.spinning} /> : <Plus size={14} />}
                <span className={styles.studyToolsDocName}>{uploading ? 'Uploading...' : 'Upload New Document'}</span>
              </div>
            </div>
          )}
        </div>
      </div>
      

      <div className={styles.section}>
        <div 
          className={styles.sectionHeader}
          onClick={() => toggleSection('studyTools')}
        >
          <div className={styles.sectionTitle}>
            <AiEditingIcon size={20} color="#667eea" strokeWidth={1.5} />
            <span className={`${styles.sectionText} ${!isOpen ? styles.hidden : ''}`}>Study Tools</span>
          </div>
          <div className={`${styles.sectionActions} ${!isOpen ? styles.hidden : ''}`}>
            <div className={`${styles.chevron} ${expandedSections.studyTools ? styles.expanded : ''}`}>
              <ChevronRight size={14} />
            </div>
          </div>
        </div>
        
        <div className={`${styles.sectionContent} ${expandedSections.studyTools && isOpen ? styles.expanded : styles.collapsed}`}>
          <div className={styles.studyToolsList}>
            {studyTools.map((tool) => (
              <div key={tool.id} className={styles.studyToolWrapper}>
                <button
                  className={`${styles.studyToolItem} ${expandedTool === tool.id ? styles.active : ''}`}
                  onClick={() => handleStudyToolClick(tool.id)}
                  style={{ '--tool-color': tool.color }}
                >
                  <tool.icon size={18} color={tool.color} />
                  <span>{tool.label}</span>
                  <ChevronRight 
                    size={14} 
                    className={`${styles.toolChevron} ${expandedTool === tool.id ? styles.expanded : ''}`}
                  />
                </button>
                
                {/* Document dropdown for this tool */}
                <div className={`${styles.toolDocDropdown} ${expandedTool === tool.id ? styles.expanded : styles.collapsed}`}>
                  {documents.length === 0 ? (
                    <div className={styles.toolDocEmpty}>
                      <p>No documents uploaded</p>
                      <button 
                        className={styles.uploadButton}
                        onClick={triggerFileUpload}
                        disabled={uploading}
                      >
                        {uploading ? <Upload size={14} className={styles.spinning} /> : <Plus size={14} />}
                        {uploading ? 'Uploading...' : 'Upload Document'}
                      </button>
                    </div>
                  ) : (
                    <>
                      {documents.map((doc) => (
                        <div 
                          key={doc.id} 
                          className={styles.toolDocItem}
                          onClick={() => handleToolWithDocument(tool.id, doc)}
                        >
                          <FileText size={14} />
                          <span>{doc.name}</span>
                        </div>
                      ))}
                      <div 
                        className={`${styles.toolDocItem} ${styles.uploadOption}`}
                        onClick={triggerFileUpload}
                      >
                        {uploading ? <Upload size={14} className={styles.spinning} /> : <Plus size={14} />}
                        <span>{uploading ? 'Uploading...' : 'Upload New Document'}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Recently Generated Section */}
      <div className={styles.section}>
        <div 
          className={styles.sectionHeader}
          onClick={() => generatedContent.length > 0 && toggleSection('recentlyGenerated')}
        >
          <div className={styles.sectionTitle}>
            <AiFileIcon size={20} color="#a855f7" strokeWidth={1.5} />
            <span className={`${styles.sectionText} ${!isOpen ? styles.hidden : ''}`}>Recently Generated</span>
          </div>
          <div className={`${styles.sectionActions} ${!isOpen ? styles.hidden : ''}`}>
            {generatedContent.length > 0 && (
              <div className={`${styles.chevron} ${expandedSections.recentlyGenerated ? styles.expanded : ''}`}>
                <ChevronRight size={14} />
              </div>
            )}
          </div>
        </div>
        
        <div className={`${styles.sectionContent} ${expandedSections.recentlyGenerated && isOpen && generatedContent.length > 0 ? styles.expanded : styles.collapsed}`}>
          {generatedContent.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No generated content yet</p>
              <p style={{ fontSize: '11px', opacity: 0.7 }}>Generate notes or quotes from your documents</p>
            </div>
          ) : (
            <div className={styles.studyToolsDocList}>
              {generatedContent.slice(0, 10).map((item) => (
                <div 
                  key={item.id} 
                  className={styles.studyToolsDocItem}
                  onClick={() => handleOpenGeneratedContent(item)}
                >
                  {item.type === 'notes' && <Bookmark size={14} style={{ color: '#667eea' }} />}
                  {item.type === 'quotes' && <MessageCircle size={14} style={{ color: '#f093fb' }} />}
                  {item.type === 'quiz' && <HelpCircle size={14} style={{ color: '#34d399' }} />}
                  {item.type === 'flashcards' && <Layers size={14} style={{ color: '#fbbf24' }} />}
                  <span className={styles.studyToolsDocName}>{item.name}</span>
                  <div className={styles.itemActions}>
                    {generatedDeleteConfirm === item.id ? (
                      <div className={styles.deleteConfirmation}>
                        <button 
                          className={`${styles.actionButton} ${styles.confirmButton}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteGeneratedContent(item.id);
                            setGeneratedDeleteConfirm(null);
                          }}
                          title="Yes, delete"
                        >
                          Yes
                        </button>
                        <button 
                          className={`${styles.actionButton} ${styles.cancelButton}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setGeneratedDeleteConfirm(null);
                          }}
                          title="Cancel"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <>
                        <button 
                          className={styles.actionButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            const newName = prompt('Enter new name:', item.name);
                            if (newName && newName !== item.name) {
                              renameGeneratedContent(item.id, newName);
                            }
                          }}
                          title="Rename"
                        >
                          <Edit3 size={12} />
                        </button>
                        <button 
                          className={styles.actionButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            setGeneratedDeleteConfirm(item.id);
                          }}
                          title="Delete"
                        >
                          <Trash2 size={12} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <ResourceSection
        title="Resources"
        icon={Files02Icon}
        items={[]}
        type="resources"
        sectionKey="resources"
        color="#f59e0b"
        isOpen={isOpen}
      />
    </div>
  );
};

export default ResourcesSidebar;
